import { useMemo } from 'react';
// redux
import { useSelector } from 'react-redux';
// components
import SvgColor from 'src/components/svg-color';
// hooks
import { useDynamicNavData } from 'src/hooks/useDynamicNavData';
// routes
import { paths } from 'src/routes/paths';


// ----------------------------------------------------------------------

const icon = (name) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
  setting: icon('settings'),
};

// ----------------------------------------------------------------------


// ── Badge injection helper ─────────────────────────────────────────────────────
/**
 * Recursively injects `info` badge count into the nav item matching `targetPath`
 */
function injectBadge(items, targetPath, count) {
  if (!count || count === 0) return items;
  return items.map((item) => {
    if (item.path === targetPath) {
      return { ...item, info: count > 99 ? '99+' : String(count) };
    }
    if (item.children) {
      return { ...item, children: injectBadge(item.children, targetPath, count) };
    }
    return item;
  });
}

// ----------------------------------------------------------------------

export function useNavData() {
  const { navData: dynamicNavData, loading } = useDynamicNavData();
  const totalUnread = useSelector((s) => s.chat?.totalUnread || 0);

  const data = useMemo(() => {
    // While loading or if no data yet, return empty (no static fallback)
    if (loading || !dynamicNavData || dynamicNavData.length === 0) {
      return [];
    }

    // Inject unread badge on chat nav item
    if (totalUnread > 0) {
      return dynamicNavData.map((section) => ({
        ...section,
        items: injectBadge(section.items || [], paths.dashboard.chat, totalUnread),
      }));
    }

    return dynamicNavData;
  }, [dynamicNavData, loading, totalUnread]);

  return data;
}
